import React, { useState, useEffect, useMemo } from "react"
import * as url from "../../helpers/url_helper"
import {
    Container,
    Row,
    Col,
    Card,
    Alert,
    CardBody,
    Form,
    Label,
    Input,
    FormFeedback,
} from "reactstrap"

// Formik Validation
import * as Yup from "yup"
import { useFormik } from "formik"

//redux
import { useSelector, useDispatch } from "react-redux"

import { withRouter } from "react-router-dom"

//Import Breadcrumb
import Breadcrumb from "../../components/Common/Breadcrumb"

import avatar from "../../assets/images/users/avatar-1.jpg"
// actions
import {
    getUserProfileInfo,
    updateUserProfile,
    resetProfileFlag,
} from "../../store/auth/profile/actions"
import {
    userResetForgetPassword,
} from "../../store/actions"

// Common Components
import TextInput from "../../components/Common/TextInput"
import SelectInput from "../../components/Common/SelectInput"
import CButton from "../../components/Common/CButton"
import AccessControlWrapper from "../../components/Common/PermissionButton"
import ConfirmButton from "../../components/Common/ConfirmButton"
import { validateBeforeConfirm } from "../../helpers/validation_helper"
import { convertFileToBase64, validateImageFile } from "../../helpers/file_helper"
import { uploadBase64File, sendEmailVerificationCode, validateEmailVerificationCode } from "../../helpers/backend_helper"
import PopupModal from "../../components/Common/PopupModel"
import { toast } from "sonner"

const UserProfile = props => {
    //meta title
    document.title = "Profile"

    const dispatch = useDispatch()

    const [readOnly, setReadOnly] = useState(true)
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false)
    const [photoUploadError, setPhotoUploadError] = useState("")

    // Email verification states
    const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false)
    const [verificationCode, setVerificationCode] = useState("")
    const [isSendingCode, setIsSendingCode] = useState(false)
    const [isVerifyingCode, setIsVerifyingCode] = useState(false)
    const [isEmailVerified, setIsEmailVerified] = useState(false)

    const {
        userProfileInfo,
        loading,
        error,
        success,
        isUpdateSuccess,
        appInfo,
        resetError
    } = useSelector(state => ({
        userProfileInfo: state.Profile.userProfileInfo,
        loading: state.Profile.loading,
        error: state.Profile.error?.message || state.Profile.error?.description || state.Profile.error,
        success: state.Profile.success,
        isUpdateSuccess: state.Profile.isUpdateSuccess,
        appInfo: state.appInfo.appInfo,
        resetError: state.ForgetPassword?.resetError?.message || state.ForgetPassword?.resetError?.description,
    }))

    // Load user profile on component mount
    useEffect(() => {
        dispatch(getUserProfileInfo())
    }, [dispatch])

    // Reset form to readonly after successful update
    useEffect(() => {
        if (isUpdateSuccess) {
            setReadOnly(true)

            // Reload profile data after successful update
            dispatch(getUserProfileInfo())
            setTimeout(() => {
                dispatch(resetProfileFlag())
            }, 3000)
        }
    }, [isUpdateSuccess, dispatch])

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            firstName: userProfileInfo?.firstName || "",
            middleName: userProfileInfo?.middleName || "",
            lastName: userProfileInfo?.lastName || "",
            displayName: userProfileInfo?.displayName || "",
        },
        validationSchema: Yup.object({
            firstName: Yup.string()
                .max(255, "First Name cannot be greater than 255 characters"),
            middleName: Yup.string()
                .max(255, "Middle Name cannot be greater than 255 characters"),
            lastName: Yup.string()
                .max(255, "Last Name cannot be greater than 255 characters"),
            displayName: Yup.string()
                .max(255, "Display Name cannot be greater than 255 characters"),
        }),
        onSubmit: async (values) => {
            const isValid = await validateBeforeConfirm(validation)
            if (isValid) {
                handleConfirmedSubmit()
            }
        },
    })

    // Function to handle the actual form submission after confirmation
    const handleConfirmedSubmit = () => {
        const values = validation.values
        // Only send fields that can be updated according to API
        const updateData = {
            firstName: values.firstName,
            middleName: values.middleName,
            lastName: values.lastName,
            displayName: values.displayName,
        }

        dispatch(updateUserProfile(updateData))
    }

    const handleChange = e => {
        validation.handleChange(e)

    }

    // Handle profile photo upload
    const handlePhotoUpload = async (event) => {
        const file = event.target.files[0]
        if (!file) return

        setIsUploadingPhoto(true)
        setPhotoUploadError("")

        try {
            // Validate image file - 550x550px, 1MB limit
            await validateImageFile(file, "profile photo", 550, 550, 1)

            // Convert to base64
            const base64Img = await convertFileToBase64(file)

            // Prepare file object for upload
            const fileObj = {
                ...file,
                preview: URL.createObjectURL(file),
                forExistingResource: true,
                resourceID: userProfileInfo?.userID,
                base64encodedFileByteArray: base64Img,
                resourceType: "CALLISTO_USER_PHOTO",
            }

            const uploadResponse = await uploadBase64File(fileObj, {})

            if (uploadResponse?.fileName) {
                // Reload profile data to get updated photo
                dispatch(getUserProfileInfo())
            } else {
                throw new Error("Upload failed - no filename returned")
            }
        } catch (error) {
            console.error('Photo upload error:', error)
            setPhotoUploadError(error.message || "Photo upload failed")
        } finally {
            setIsUploadingPhoto(false)
            // Clear the input
            event.target.value = ''
        }
    }

    // Handle sending verification code
    const handleSendVerificationCode = async () => {
        if (!userProfileInfo?.email) {
            toast.error("Email not found")
            return
        }

        setIsSendingCode(true)
        try {
            await sendEmailVerificationCode({
                userIdentity: userProfileInfo.email
            })
            setIsVerificationModalOpen(true)
            toast.success("Verification code sent to your email")
        } catch (error) {
            console.error('Send verification code error:', error)
            toast.error(error.message || "Failed to send verification code")
        } finally {
            setIsSendingCode(false)
        }
    }

    // Handle verifying code
    const handleVerifyCode = async () => {
        if (!verificationCode || verificationCode.length !== 6) {
            toast.error("Please enter a valid 6-digit verification code")
            return
        }

        setIsVerifyingCode(true)
        try {
            await validateEmailVerificationCode({
                userIdentity: userProfileInfo.email,
                verificationCode: verificationCode
            })
            setIsEmailVerified(true)
            setIsVerificationModalOpen(false)
            setVerificationCode("")
            toast.success("Email verified successfully!")
        } catch (error) {
            console.error('Verify code error:', error)
            toast.error(error.message || "Invalid verification code")
        } finally {
            setIsVerifyingCode(false)
        }
    }

    // Close verification modal
    const closeVerificationModal = () => {
        setIsVerificationModalOpen(false)
        setVerificationCode("")
    }


    

    // Password reset form validation
    const passwordValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            oldPassword: "",
            newPassword: "",
            confirmPassword: "",
        },
        validationSchema: Yup.object({
            oldPassword: Yup.string().required("Please Enter Your Old Password"),
            newPassword: Yup.string().required("Please Enter Your New Password"),
            confirmPassword: Yup.string()
                .required("Please Confirm Your New Password")
                .oneOf([Yup.ref('newPassword'), null], "Passwords must match"),
        }),
        onSubmit: values => {
            dispatch(userResetForgetPassword(values, props.history))
        },
    })

    return (
        <React.Fragment>
            <style>
                {`
          .profile-photo-container:hover .upload-overlay {
            opacity: 1 !important;
          }
        `}
            </style>
            <div className="page-content">
                <Container fluid>
                    {/* Render Breadcrumb */}
                    <Breadcrumb title="Dashboard" breadcrumbItem="Profile" />

                    <Row>
                        <Col lg="12">
                            {error && error ? <Alert color="danger">{error}</Alert> : null}
                            {success ? <Alert color="success">{success}</Alert> : null}

                            {/* Profile Header Card */}
                            <Card>
                                <CardBody>
                                    <div className="d-flex">
                                        <div className="me-4 position-relative" style={{ display: 'inline-block' }}>
                                            <div
                                                className="profile-photo-container"
                                                style={{
                                                    position: 'relative',
                                                    display: 'inline-block',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {userProfileInfo?.photo ? (
                                                    <img
                                                        src={userProfileInfo?.photo}
                                                        alt="Profile"
                                                        className="avatar-md rounded-circle img-thumbnail"
                                                        style={{
                                                            transition: 'opacity 0.3s ease'
                                                        }}
                                                    />
                                                ) : <div className="avatar-md rounded-circle header-profile-user d-flex justify-content-center align-items-center fw-bold"> {userProfileInfo?.displayName?.[0]} </div> || ""}



                                                {/* Upload overlay - shows on hover */}
                                                <div
                                                    className="upload-overlay"
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        opacity: 0,
                                                        transition: 'opacity 0.3s ease',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => !isUploadingPhoto && document.getElementById('profile-photo-input').click()}
                                                >
                                                    {isUploadingPhoto ? (
                                                        <div className="spinner-border spinner-border-sm text-white" />
                                                    ) : (
                                                        <i className="bx bx-camera text-white" style={{ fontSize: '24px' }} />
                                                    )}
                                                </div>
                                            </div>

                                            {/* Hidden file input */}
                                            <input
                                                id="profile-photo-input"
                                                type="file"
                                                accept="image/*"
                                                onChange={handlePhotoUpload}
                                                style={{ display: 'none' }}
                                                disabled={isUploadingPhoto}
                                            />
                                        </div>
                                        <div className="flex-grow-1 align-self-center">
                                            <div className="text-muted">
                                                <h5>{userProfileInfo?.displayName || userProfileInfo?.firstName}</h5>
                                                <p className="mb-1">{userProfileInfo?.email}</p>
                                                {photoUploadError && (
                                                    <p className="text-danger mb-1" style={{ fontSize: '12px' }}>{photoUploadError}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    <Row>
                        <Col lg="12">
                            {/* Edit Button */}
                            {readOnly && (
                                <div className="mb-3 d-flex justify-content-end">
                                    <AccessControlWrapper
                                        actionType={"post"}
                                        contextUri={url.UPDATE_USER_PROFILE}
                                    >
                                        <CButton.Primary
                                            text="Edit Profile"
                                            onClick={() => setReadOnly(false)}
                                        />
                                    </AccessControlWrapper>
                                </div>
                            )}

                            {/* Profile Information Card */}
                            <Card>
                                <CardBody>
                                    <Form
                                        className="needs-validation"
                                        onSubmit={e => {
                                            e.preventDefault()
                                            validation.handleSubmit()
                                            return false
                                        }}
                                    >
                                        <Row>
                                            <Col md="6">
                                                <TextInput
                                                    label="First Name"
                                                    name="firstName"
                                                    placeholder="Enter First Name"
                                                    type="text"
                                                    maxLength={255}
                                                    readOnly={readOnly}
                                                    validation={validation}
                                                    handleChange={handleChange}
                                                />
                                            </Col>
                                            <Col md="6">
                                                <TextInput
                                                    label="Middle Name"
                                                    name="middleName"
                                                    placeholder="Enter Middle Name"
                                                    type="text"
                                                    maxLength={255}
                                                    readOnly={readOnly}
                                                    validation={validation}
                                                    handleChange={handleChange}
                                                />
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col md="6">
                                                <TextInput
                                                    label="Last Name"
                                                    name="lastName"
                                                    placeholder="Enter Last Name"
                                                    type="text"
                                                    maxLength={255}
                                                    readOnly={readOnly}
                                                    validation={validation}
                                                    handleChange={handleChange}
                                                />
                                            </Col>
                                            <Col md="6">
                                                <TextInput
                                                    label="Display Name"
                                                    name="displayName"
                                                    placeholder="Enter Display Name"
                                                    type="text"
                                                    maxLength={255}
                                                    readOnly={readOnly}
                                                    validation={validation}
                                                    handleChange={handleChange}
                                                />
                                            </Col>
                                        </Row>

                                        <Row>

                                            <Col md="6">
                                                <div className="mb-3">
                                                    <label className="form-label">Email</label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div className="form-control input-as-text flex-grow-1">
                                                            {userProfileInfo?.email || "Email not found"}
                                                        </div>
                                                        {!isEmailVerified && userProfileInfo?.emailVerified === "false" && userProfileInfo?.email && (
                                                            <AccessControlWrapper
                                                                actionType={"post"}
                                                                contextUri={url.SEND_VERIFICATION_CODE}
                                                            >
                                                                <CButton.Info
                                                                    text="Verify"
                                                                    loading={isSendingCode}
                                                                    onClick={handleSendVerificationCode}
                                                                    disabled={isSendingCode}
                                                                />
                                                            </AccessControlWrapper>
                                                        )}
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md="6">
                                                <div className="mb-3">
                                                    <label className="form-label">Mobile</label>
                                                    <div className="form-control input-as-text">
                                                        {userProfileInfo?.mobile || "Mobile not found"}
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>

                                        {/* Form Action Buttons */}
                                        {!readOnly && (
                                            <div className="d-flex gap-2 justify-content-end mt-4">
                                                <CButton.Secondary
                                                    text="Cancel"
                                                    onClick={() => {
                                                        setReadOnly(true)

                                                        validation.resetForm()
                                                    }}
                                                />

                                                <ConfirmButton
                                                    validateBeforeConfirm={() =>
                                                        validateBeforeConfirm(validation)
                                                    }
                                                    onConfirm={handleConfirmedSubmit}
                                                    message="Are you sure you want to update your profile? Please review all details before proceeding."

                                                    renderButton={
                                                        <CButton.Primary
                                                            text="Update Profile"
                                                            disabled={loading}
                                                        />
                                                    }
                                                />
                                            </div>
                                        )}
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Password Reset Section */}
                    <Row>
                        <Col lg="12">
                            <Card>
                                <CardBody>
                                    <h5 className="card-title mb-4">Change Password</h5>
                                    <Form
                                        className="form-horizontal"
                                        onSubmit={e => {
                                            e.preventDefault()
                                            passwordValidation.handleSubmit()
                                            return false
                                        }}
                                    >
                                        <div className="mb-3">
                                            <Label className="form-label">Old Password</Label>
                                            <Input
                                                name="oldPassword"
                                                className="form-control"
                                                placeholder="Enter your old password"
                                                type="password"
                                                onChange={passwordValidation.handleChange}
                                                onBlur={passwordValidation.handleBlur}
                                                value={passwordValidation.values.oldPassword || ""}
                                                invalid={
                                                    passwordValidation.touched.oldPassword &&
                                                        passwordValidation.errors.oldPassword
                                                        ? true
                                                        : false
                                                }
                                            />
                                            {passwordValidation.touched.oldPassword &&
                                                passwordValidation.errors.oldPassword ? (
                                                <FormFeedback type="invalid">
                                                    {passwordValidation.errors.oldPassword}
                                                </FormFeedback>
                                            ) : null}
                                        </div>

                                        <div className="mb-3">
                                            <Label className="form-label">New Password</Label>
                                            <Input
                                                name="newPassword"
                                                className="form-control"
                                                placeholder="Enter your new password"
                                                type="password"
                                                onChange={passwordValidation.handleChange}
                                                onBlur={passwordValidation.handleBlur}
                                                value={passwordValidation.values.newPassword || ""}
                                                invalid={
                                                    passwordValidation.touched.newPassword &&
                                                        passwordValidation.errors.newPassword
                                                        ? true
                                                        : false
                                                }
                                            />
                                            {passwordValidation.touched.newPassword &&
                                                passwordValidation.errors.newPassword ? (
                                                <FormFeedback type="invalid">
                                                    {passwordValidation.errors.newPassword}
                                                </FormFeedback>
                                            ) : null}
                                        </div>

                                        <div className="mb-3">
                                            <Label className="form-label">Confirm New Password</Label>
                                            <Input
                                                name="confirmPassword"
                                                className="form-control"
                                                placeholder="Confirm your new password"
                                                type="password"
                                                onChange={passwordValidation.handleChange}
                                                onBlur={passwordValidation.handleBlur}
                                                value={passwordValidation.values.confirmPassword || ""}
                                                invalid={
                                                    passwordValidation.touched.confirmPassword &&
                                                        passwordValidation.errors.confirmPassword
                                                        ? true
                                                        : false
                                                }
                                            />
                                            {passwordValidation.touched.confirmPassword &&
                                                passwordValidation.errors.confirmPassword ? (
                                                <FormFeedback type="invalid">
                                                    {passwordValidation.errors.confirmPassword}
                                                </FormFeedback>
                                            ) : null}
                                        </div>

                                        {resetError && resetError ? (
                                            <Alert color="danger" style={{ marginTop: "13px" }}>
                                                {resetError}
                                            </Alert>
                                        ) : null}

                                        <div className="text-end">
                                            <AccessControlWrapper
                                                actionType={"post"}
                                                contextUri={url.POST_RESET_PASSWORD}
                                            >
                                                <CButton.Primary
                                                    text="Reset Password"
                                                    type="button"
                                                    onClick={() => passwordValidation.handleSubmit()}
                                                />
                                            </AccessControlWrapper>
                                        </div>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* Email Verification Modal */}
            <PopupModal
                isOpen={isVerificationModalOpen}
                toggle={closeVerificationModal}
                title="Verify Email Address"
                size="md"
                footerButtons={
                    <div className="d-flex gap-2">
                        <CButton.Secondary
                            text="Cancel"
                            onClick={closeVerificationModal}
                        />
                        <AccessControlWrapper
                            actionType={"post"}
                            contextUri={url.VALIDATE_VERIFICATION_CODE}
                        >
                            <CButton.Primary
                                text="Verify Code"
                                loading={isVerifyingCode}
                                onClick={handleVerifyCode}
                                disabled={isVerifyingCode || !verificationCode}
                            />
                        </AccessControlWrapper>
                    </div>
                }
            >
                <div className="mb-3">
                    <p className="text-muted mb-3">
                        We've sent a 6-digit verification code to <strong>{userProfileInfo?.email}</strong>.
                        Please enter the code below to verify your email address.
                    </p>
                    <Label className="form-label">Verification Code</Label>
                    <Input
                        type="text"
                        className="form-control text-center"
                        placeholder="Enter 6-digit code"
                        value={verificationCode}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                            setVerificationCode(value)
                        }}
                        maxLength={6}
                        style={{ fontSize: '18px', letterSpacing: '2px' }}
                    />
                    <small className="text-muted">
                        Please check your email inbox and spam folder for the verification code.
                    </small>
                </div>
            </PopupModal>
        </React.Fragment>
    )
}

export default withRouter(UserProfile)
